/**
 * The one rule for a tenant's own widget stylesheet, in the one place all three
 * readers can import it: the schema's neighbour, the server that serves it
 * inside the widget, and the console that lets a person type it.
 *
 * It lived privately in apps/server/src/ui/widget.ts, which meant a console
 * editor could only learn that a paste was refused by saving it and watching
 * nothing change, since the widget drops a refused block silently. A security
 * regex copied into a second file is how two copies come to disagree, so it
 * moved here rather than being restated.
 *
 * The stylesheet is served inside the widget's <style>, under the widget's CSP
 * (no external resources, no scripts). What it may NOT do is reach out: no
 * @import, no url(), no behavior/expression, and no closing the style element.
 * A block that tries any of those is refused whole rather than trimmed, because
 * a partially applied stylesheet is harder to reason about than none.
 */

export const CUSTOM_CSS_MAX = 20_000;

const REACHES_OUT: ReadonlyArray<readonly [RegExp, string]> = [
  [/@import/i, '@import'],
  [/url\s*\(/i, 'url()'],
  [/expression\s*\(/i, 'expression()'],
  [/behavior\s*:/i, 'behavior:'],
  [/<\/?\s*style/i, 'a <style> tag'],
  [/javascript:/i, 'javascript:'],
  [/-moz-binding/i, '-moz-binding'],
];

export type CustomCssCheck = { ok: true; css: string } | { ok: false; reason: string };

/** Accepts the stylesheet, trimmed, or names the first construct that refuses it. */
export function checkCustomCss(css: string | null | undefined): CustomCssCheck {
  const trimmed = (css ?? '').trim();
  if (trimmed.length > CUSTOM_CSS_MAX) {
    return { ok: false, reason: `the stylesheet is ${trimmed.length} characters; the ceiling is ${CUSTOM_CSS_MAX}` };
  }
  for (const [pattern, name] of REACHES_OUT) {
    if (pattern.test(trimmed)) {
      return { ok: false, reason: `${name} is not allowed: a widget stylesheet may not load or run anything` };
    }
  }
  return { ok: true, css: trimmed };
}
